/**
 * gift-wrap-guard.js — Ecom Studio, 2026-08-09
 *
 * PROBLEMA QUE RESUELVE (reproducido a partir del pedido #168505):
 *
 *   1. El checkbox de envoltura empuja la envoltura con `quantity: 1` fijo
 *      (ver assets/product-cart.js, addToCart). Nunca agrega más de una.
 *   2. Pero `/cart/add.js` ACUMULA. Cada clic en "Añadir a la cesta" con la
 *      casilla marcada suma +1 envoltura.
 *   3. La línea del producto padre lleva una propiedad `Nota` con un contador
 *      regresivo que cambia cada minuto, así que el padre se PARTE en líneas
 *      separadas — mientras la envoltura, cuya única propiedad es
 *      `For: <título>`, es estable y SIEMPRE se fusiona en una línea que crece.
 *   4. Al bajar la cantidad del padre con el `−`, la envoltura NO baja: el
 *      `parent_id` de Shopify solo sincroniza al ELIMINAR la línea, no al
 *      cambiar su cantidad.
 *
 *   Resultado: carritos con 1 producto y 4 envolturas.
 *
 * QUÉ HACE ESTE ARCHIVO:
 *
 *   Después de cada mutación del carrito, recorta cada línea de envoltura para
 *   que nunca exceda las unidades del producto al que dice pertenecer (`For`).
 *   Nunca agrega envolturas y nunca toca líneas que no sean la envoltura.
 *
 * QUÉ NO HACE, A PROPÓSITO:
 *
 *   No interviene en el checkout. Un fallo aquí bloquearía ventas, que es peor
 *   que el problema original. El checkout exprés (PayPal desde la ficha) queda
 *   fuera de alcance y se cubre con una Shopify Function de validación.
 */
(function () {
  'use strict';

  var cfg = window.EurusGiftWrapGuard;
  if (!cfg || !cfg.variantIds || !cfg.variantIds.length) return;

  var WRAP_VARIANTS = cfg.variantIds.map(Number);
  var running = false;

  function isWrap(item) {
    return WRAP_VARIANTS.indexOf(Number(item.variant_id)) !== -1;
  }

  /** Unidades en el carrito del producto al que apunta `For`. */
  function unitsOfParent(items, forTitle) {
    return items.reduce(function (sum, it) {
      if (isWrap(it)) return sum;
      var matches = it.product_title === forTitle || it.title === forTitle;
      return matches ? sum + it.quantity : sum;
    }, 0);
  }

  /**
   * Se direcciona por ÍNDICE de línea, no por `key`.
   *
   * Dos líneas de envoltura con la misma variante y las mismas propiedades
   * comparten el mismo `key`, aunque el carrito las mantenga separadas por su
   * `parent_id`. Un `change.js` por `key` se aplica entonces a AMBAS líneas: al
   * intentar bajar una de 4 a 3, la otra subía de 1 a 3. El índice es unívoco.
   */
  function changeLine(index, quantity) {
    return fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: index, quantity: quantity })
    });
  }

  /**
   * El tope se aplica al GRUPO de líneas de envoltura que comparten el mismo
   * `For`, no a cada línea por separado: dos líneas de 4 y 1 pasan el filtro
   * individualmente contra un padre de 4 unidades, pero sumadas son 5.
   * Se recorta empezando por la línea más grande.
   */
  /** Devuelve UN solo recorte pendiente, o null si el carrito ya está sano. */
  function nextFix(cart) {
    var groups = {};

    cart.items.forEach(function (item, i) {
      if (!isWrap(item)) return;
      // Sin `For` no podemos atarla a un padre. La dejamos en paz antes que
      // arriesgarnos a borrar una envoltura legítima.
      var forTitle = item.properties && item.properties.For;
      if (!forTitle) return;
      (groups[forTitle] = groups[forTitle] || []).push({ line: i + 1, quantity: item.quantity });
    });

    var pending = null;

    Object.keys(groups).some(function (forTitle) {
      var lines = groups[forTitle];
      var total = lines.reduce(function (s, l) { return s + l.quantity; }, 0);
      var excess = total - unitsOfParent(cart.items, forTitle);
      if (excess <= 0) return false;

      // Se recorta empezando por la línea más grande.
      var biggest = lines.reduce(function (a, b) {
        return b.quantity > a.quantity ? b : a;
      });
      pending = {
        line: biggest.line,
        quantity: Math.max(0, biggest.quantity - excess)
      };
      return true;
    });

    return pending;
  }

  /**
   * Aplica un recorte a la vez, releyendo el carrito entre cada uno: al bajar
   * una línea a 0 desaparece y los índices de las siguientes se recorren.
   */
  function normalize() {
    if (running) return Promise.resolve(false);
    running = true;

    var changed = false;

    function step(attemptsLeft) {
      if (attemptsLeft <= 0) return Promise.resolve();

      return fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var fix = nextFix(cart);
          if (!fix) return;
          changed = true;
          return changeLine(fix.line, fix.quantity).then(function () {
            return step(attemptsLeft - 1);
          });
        });
    }

    return step(10)
      .then(function () { return changed; })
      .catch(function (err) {
        // Nunca romper el carrito por culpa del guardián.
        console.warn('[gift-wrap-guard] no se pudo normalizar:', err);
        return false;
      })
      .then(function (changed) {
        running = false;
        if (changed) {
          document.dispatchEvent(new CustomEvent('eurus:cart:reload'));
        }
        return changed;
      });
  }

  // --- Enganche: correr después de cada mutación del carrito -----------------

  var CART_MUTATIONS = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
  var nativeFetch = window.fetch;

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var promise = nativeFetch.apply(this, arguments);

    if (!running && CART_MUTATIONS.test(url)) {
      promise.then(function () {
        // Al final de la cola: dejamos que el tema pinte su respuesta primero.
        setTimeout(normalize, 0);
      }, function () { /* la petición falló; no hay nada que normalizar */ });
    }

    return promise;
  };

  // Carritos que ya venían mal de una sesión anterior.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', normalize);
  } else {
    normalize();
  }

  window.EurusGiftWrapGuard.normalize = normalize;
})();
